import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import {
  TrendingUp, DollarSign, Target, CheckCircle, ArrowUpRight, Loader2,
  ArrowDownRight, Layers, Zap,
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import DateRangeSelect from '@/components/DateRangeSelect';
import SiteDeptFilter from '@/components/SiteDeptFilter';

const PIE_COLORS = ['#06b6d4', '#8b5cf6'];

const SavingsTracker = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('12m');
  const [filterSite, setFilterSite] = useState('All Sites');
  const [filterDept, setFilterDept] = useState('All Departments');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/savings-tracker/overview');
        setData(res.data);
      } catch {
        toast.error('Failed to load savings data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout title="Savings Tracker" subtitle="Realized vs Projected savings across all BOMs">
        <div className="flex items-center justify-center py-24" data-testid="savings-tracker-loading">
          <Loader2 className="h-12 w-12 animate-spin text-green-600" />
        </div>
      </Layout>
    );
  }

  const s = data?.summary || {};
  const trend = data?.monthly_trend || [];
  const byBom = data?.by_bom || [];
  const recent = data?.recent_approvals || [];

  const savingsTypePie = [
    { name: 'Same-Make', value: s.same_make_realized || 0 },
    { name: 'FFF Alternate', value: s.fff_realized || 0 },
  ].filter(d => d.value > 0);

  // Cumulative trend
  let cumRealized = 0;
  let cumProjected = 0;
  const projectedMonthly = s.total_projected > 0 ? s.total_projected / Math.max(trend.length, 1) : 0;
  const cumulativeTrend = trend.map(t => {
    cumRealized += t.realized;
    cumProjected += projectedMonthly;
    return { month: t.month.slice(5), realized: Math.round(cumRealized), projected: Math.round(cumProjected), net: Math.round(cumRealized - (t.switching_cost || 0)) };
  });

  return (
    <Layout title="Savings Tracker" subtitle="Realized vs Projected savings across all BOMs">
      <div className="space-y-6" data-testid="savings-tracker-page">
        {/* Filters */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-3 pb-3">
            <div className="flex gap-3 flex-wrap items-center">
              <DateRangeSelect value={dateRange} onChange={setDateRange} testId="savings-date-range" />
              <SiteDeptFilter site={filterSite} onSiteChange={setFilterSite} dept={filterDept} onDeptChange={setFilterDept} />
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="savings-kpis">
          <Card className="bg-blue-50 border-blue-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-blue-600 flex items-center gap-1"><Target className="h-3 w-3" /> Projected <InfoTip text="Total savings potential identified across all BOMs." /></p>
              <p className="text-xl font-bold text-blue-700">${s.total_projected?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-green-600 flex items-center gap-1"><DollarSign className="h-3 w-3" /> Realized <InfoTip text="Savings achieved from approved alternates." /></p>
              <p className="text-xl font-bold text-green-700">${s.total_realized?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-red-600 flex items-center gap-1"><ArrowDownRight className="h-3 w-3" /> Switching Cost <InfoTip text="Cost of implementing alternate components." /></p>
              <p className="text-xl font-bold text-red-700">${s.total_switching_cost?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-emerald-600 flex items-center gap-1"><ArrowUpRight className="h-3 w-3" /> Net Savings <InfoTip text="Realized minus switching costs." /></p>
              <p className="text-xl font-bold text-emerald-700">${s.total_net_savings?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-amber-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</p>
              <p className="text-xl font-bold text-amber-700">{s.total_approved || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] text-purple-600 flex items-center gap-1"><Zap className="h-3 w-3" /> Realization % <InfoTip text="How much of projected savings has been realized." /></p>
              <p className="text-xl font-bold text-purple-700">{s.realization_rate || 0}%</p>
              <div className="w-full bg-purple-200 rounded-full h-1.5 mt-1">
                <div className="bg-purple-600 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(s.realization_rate || 0, 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cumulative Trend */}
          <Card className="bg-white border-gray-200 shadow-sm lg:col-span-2">
            <CardHeader><CardTitle className="text-gray-900 text-base flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-600" /> Savings Trend (Cumulative)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={cumulativeTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                  <RTooltip formatter={v => `$${v.toLocaleString()}`} />
                  <Legend />
                  <Line type="monotone" dataKey="projected" name="Projected" stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={false} />
                  <Line type="monotone" dataKey="realized" name="Realized" stroke="#22c55e" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#10b981" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Savings Type Pie */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader><CardTitle className="text-gray-900 text-base flex items-center gap-2"><Layers className="h-5 w-5 text-purple-600" /> Savings by Type</CardTitle></CardHeader>
            <CardContent>
              {savingsTypePie.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={savingsTypePie} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {savingsTypePie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                      </Pie>
                      <RTooltip formatter={v => `$${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6 mt-2">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-cyan-500" /><span className="text-xs text-gray-600">Same-Make</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500" /><span className="text-xs text-gray-600">FFF Alternate</span></div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm text-center py-12">No savings data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown Bar */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader><CardTitle className="text-gray-900 text-base">Monthly Savings Breakdown</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trend.map(t => ({ ...t, month: t.month.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => `$${v}`} />
                <RTooltip formatter={v => `$${v.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="realized" name="Realized Savings" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="switching_cost" name="Switching Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="net" name="Net Savings" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* BOM Breakdown Table */}
        {byBom.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="savings-bom-breakdown">
            <CardHeader><CardTitle className="text-gray-900 text-base">Savings by BOM</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>BOM</TableHead>
                    <TableHead>Approved</TableHead>
                    <TableHead>Realized Savings</TableHead>
                    <TableHead>Switching Cost</TableHead>
                    <TableHead>Net Savings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byBom.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-semibold text-sm">{b.bom_name}</TableCell>
                      <TableCell><Badge variant="outline">{b.approved_count}</Badge></TableCell>
                      <TableCell className="text-green-700 font-semibold">${b.realized_savings.toLocaleString()}</TableCell>
                      <TableCell className="text-red-600">${b.switching_cost.toLocaleString()}</TableCell>
                      <TableCell className="text-emerald-700 font-bold">${b.net_savings.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent Approvals */}
        {recent.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="savings-recent-approvals">
            <CardHeader><CardTitle className="text-gray-900 text-base">Recent Approvals</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>ID</TableHead>
                    <TableHead>Original MPN</TableHead>
                    <TableHead>Alternate MPN</TableHead>
                    <TableHead>Savings</TableHead>
                    <TableHead>Switching</TableHead>
                    <TableHead>Net</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{a.approval_id}</TableCell>
                      <TableCell className="font-mono text-sm">{a.original_mpn}</TableCell>
                      <TableCell className="font-mono text-sm text-green-700">{a.alternate_mpn}</TableCell>
                      <TableCell className="text-green-700 font-semibold">${(a.savings_value || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-red-600">${(a.switching_cost || 0).toLocaleString()}</TableCell>
                      <TableCell className="font-bold text-emerald-700">${(a.net_savings || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-xs text-gray-500">{(a.created_at || '').slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SavingsTracker;
